import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAssistant } from "@/context/AssistantContext";
import { useContact } from "@/context/ContactContext";
import { trackEvent } from "@/utils/analytics";
import { stripLocalePrefix, localeFromPathname, withLocale } from "@/i18n/locales";
import "./AssistantConversation.scss";


const SUGGESTION_KEYS = ["roi", "scope", "rag"] as const;

interface Props {
  /** "inline" = hero terminal, "dock" = floating panel */
  surface?: "inline" | "dock";
}

const AssistantConversation: React.FC<Props> = ({ surface = "inline" }) => {
  const { t } = useTranslation();
  const { history, busy, streaming, ask, close } = useAssistant();
  const { requestContact } = useContact();
  const location = useLocation();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [input, setInput] = React.useState("");

  const hasAnswer = history.some((l) => l.type === "out");

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, busy]);

  const handleCtaClick = () => {
    const lastQuestion =
      [...history].reverse().find((l) => l.type === "in")?.text ?? "";
    const prefill = t("aiFeature.terminal.cta.prefill", { question: lastQuestion });
    trackEvent("cta_click", { location: "assistant" });

    const stripped = stripLocalePrefix(location.pathname);
    const onLanding = stripped === "/";
    if (!onLanding) {
      requestContact(prefill);
      close();
      navigate(withLocale(localeFromPathname(location.pathname), "/") + "#contact-section");
    } else {
      requestContact(prefill);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    ask(input, "typed");
    setInput("");
  };

  const suggestions = SUGGESTION_KEYS.map((k, i) => ({
    key: k,
    label: t(`aiFeature.terminal.suggestions.${i}`),
  }));

  return (
    <div className="ac-body" ref={scrollRef} data-surface={surface}>
      {history.map((line, i) => {
        if (line.type === "sys")
          return (
            <div key={i} className="t-line t-dim">
              {"//"} {line.text}
            </div>
          );
        if (line.type === "in")
          return (
            <div key={i} className="t-line">
              <span className="t-prompt">›</span>
              <span>{line.text}</span>
            </div>
          );
        if (line.type === "out")
          return (
            <div
              key={i}
              className="t-line t-ok"
              style={{ marginTop: 4, marginBottom: 10 }}
            >
              <span style={{ opacity: 0.7 }}>↪</span>
              <span>{line.text}</span>
            </div>
          );
        return (
          <div key={i} className="t-line t-err">
            ✗ {line.text}
          </div>
        );
      })}
      {hasAnswer && (
        <div className="t-line t-cta" style={{ marginTop: 8 }}>
          <button type="button" className="t-cta-btn" onClick={handleCtaClick}>
            {t("aiFeature.terminal.cta.label")}
          </button>
        </div>
      )}
      {busy && !streaming && (
        <div className="t-line t-dim">
          <span className="t-accent">›</span>
          <span>
            {t("aiFeature.terminal.thinking")}
            <span className="t-cursor" />
          </span>
        </div>
      )}
      <form onSubmit={onSubmit} className="t-line" style={{ marginTop: 8 }}>
        <span className="t-prompt">›</span>
        <input
          className="t-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={busy ? "" : t("aiFeature.terminal.inputPlaceholder")}
          disabled={busy}
          aria-label={t("aiFeature.terminal.inputAriaLabel")}
        />
      </form>
      {history.length <= 2 && (
        <div className="t-suggest">
          {suggestions.map((s) => (
            <button
              key={s.key}
              onClick={() => ask(s.label, "suggestion")}
              disabled={busy}
              type="button"
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AssistantConversation;
